/**
 * Incremental parser for the Server-Sent Events wire format.
 *
 * The whole reason this is its own unit is that **a network chunk is not a frame**. A
 * `fetch` body reader hands over whatever bytes happened to arrive together, so a single
 * `data:` line can be split across two reads and a frame terminator (`\n\n`) can land
 * exactly on a chunk boundary. Parsing each chunk in isolation silently drops or corrupts
 * tokens, and only ever fails under real network conditions — never against a mock that
 * writes one frame per chunk. Hence the retained buffer, and hence `sse.test.tsx`.
 */

export type SseFrame = {
  /** The `event:` field, defaulting to SSE's `message` when a frame omits it. */
  event: string;
  /** All `data:` lines of the frame, joined with newlines as the spec requires. */
  data: string;
};

/**
 * Turns one raw frame (everything between two blank lines) into a field pair. Returns
 * `null` for frames that carry nothing we can act on — comment-only or empty blocks,
 * which servers send as keep-alives.
 */
export const parseSseFrame = (raw: string): SseFrame | null => {
  let event = 'message';
  const data: string[] = [];

  for (const line of raw.split('\n')) {
    /** A leading colon marks a comment; a blank line inside a frame is ignorable. */
    if (!line || line.startsWith(':')) continue;

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const rawValue = colon === -1 ? '' : line.slice(colon + 1);
    /** Exactly one leading space after the colon is part of the framing, not the value. */
    const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;

    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }

  if (!data.length && event === 'message') return null;

  return { event, data: data.join('\n') };
};

export class SseParser {
  /** Bytes seen so far that do not yet amount to a complete frame. */
  private buffer = '';
  /**
   * A trailing `\r` is held back rather than normalised, because the `\n` that would pair
   * with it into a single CRLF line ending may be the first byte of the next chunk.
   * Without this, `...\r` + `\n\n...` would read as three line breaks instead of two.
   */
  private carry = '';

  /** Feeds one chunk in and returns every frame that is now complete. */
  public push = (chunk: string): SseFrame[] => {
    let text = this.carry + chunk;
    this.carry = '';

    if (text.endsWith('\r')) {
      this.carry = '\r';
      text = text.slice(0, -1);
    }

    /** Normalise CRLF and lone CR to LF so only one terminator has to be searched for. */
    this.buffer += text.replace(/\r\n?/g, '\n');

    const frames: SseFrame[] = [];
    let boundary = this.buffer.indexOf('\n\n');

    while (boundary !== -1) {
      const raw = this.buffer.slice(0, boundary);
      this.buffer = this.buffer.slice(boundary + 2);
      const frame = parseSseFrame(raw);
      if (frame) frames.push(frame);
      boundary = this.buffer.indexOf('\n\n');
    }

    return frames;
  };

  /**
   * Call once the stream ends. A well-behaved server terminates its last frame, but one
   * that closes the connection instead would otherwise lose it in the buffer.
   */
  public flush = (): SseFrame[] => {
    const raw = (this.buffer + this.carry).replace(/\r\n?/g, '\n');
    this.buffer = '';
    this.carry = '';
    const frame = raw.trim() ? parseSseFrame(raw) : null;
    return frame ? [frame] : [];
  };
}
